/*
  Mitarbeitenden-Statistiken für Shiftplan V2.
*/

import React, { useEffect, useMemo, useState } from "react";
import { Box, InputAdornment, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import AppPersonBadge from "../../../../design-system/components/AppPersonBadge";
import { IconHoliday, IconSearch, IconTime } from "../../../../design-system/icons";
import { buildEmployeeStatsModel } from "../../domain/model/employeeStats";
import usePlanRange from "../../domain/model/usePlanRange";
import FloatingPanel from "./FloatingPanel";

const HOURS_FORMATTER = new Intl.NumberFormat("de-CH", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function formatHours(value) {
  return `${HOURS_FORMATTER.format(Number(value || 0))}h`;
}

function getPercent(value, target) {
  const numericTarget = Number(target || 0);
  if (!numericTarget) return 0;
  return Math.max(0, Math.round((Number(value || 0) / numericTarget) * 100));
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, Number(value || 0)));
}

function getRemainingLabel(plannedHours, targetHours) {
  const balance = Number(plannedHours || 0) - Number(targetHours || 0);
  if (Math.abs(balance) < 0.05) return "im Soll";
  return balance > 0 ? `${formatHours(balance)} über Soll` : `${formatHours(Math.abs(balance))} ausstehend`;
}

function getEmployeeInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "MA";
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getEmploymentPercent(employee, workingDaysPerWeek) {
  const weeklyHours = Number(employee?.weeklyHours || 0);
  const fullTimeWeeklyHours = Math.max(1, Number(workingDaysPerWeek || 5) * 8.4);
  if (!weeklyHours) return 0;
  return Math.round((weeklyHours / fullTimeWeeklyHours) * 100);
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function EmployeeList({ employees, selectedEmployeeId, query, onQueryChange, onSelect }) {
  const filteredEmployees = useMemo(() => {
    const search = normalizeText(query);
    if (!search) return employees;

    return employees.filter((employee) =>
      `${employee?.name || ""} ${employee?.email || ""}`.toLowerCase().includes(search)
    );
  }, [employees, query]);

  return (
    <Box className="spv2-employee-stats-rail">
      <Box className="spv2-employee-stats-search">
        <InputAdornment position="start">
          <IconSearch fontSize="small" />
        </InputAdornment>
        <input
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Mitarbeitende"
          aria-label="Mitarbeitende suchen"
        />
      </Box>

      <Box className="spv2-employee-stats-list" role="listbox" aria-label="Mitarbeitende">
        {filteredEmployees.map((employee) => {
          const isActive = String(employee?.id || "") === String(selectedEmployeeId || "");

          return (
            <button
              key={employee.id}
              type="button"
              className={`spv2-employee-stats-list-item${isActive ? " is-active" : ""}`}
              role="option"
              aria-selected={isActive}
              onClick={() => onSelect(employee.id)}
            >
              <AppPersonBadge
                className="spv2-employee-stats-list-avatar"
                name={employee.name}
                initials={getEmployeeInitials(employee.name)}
                size="sm"
              />
              <span className="spv2-employee-stats-list-copy">
                <span className="spv2-employee-stats-list-name">{employee.name}</span>
              </span>
            </button>
          );
        })}
      </Box>
    </Box>
  );
}

function SectionTitle({ title, hint }) {
  return (
    <Box className="spv2-employee-stats-section-title">
      <Typography component="h3">{title}</Typography>
      {hint ? <span>{hint}</span> : null}
    </Box>
  );
}

function YearGauge({ plannedHours, targetHours, vacationDays }) {
  const percent = getPercent(plannedHours, targetHours);

  return (
    <Box className="spv2-employee-stats-gauge-block">
      <Box
        className="spv2-employee-stats-gauge"
        style={{ "--spv2-employee-stats-gauge": `${clampPercent(percent)}%` }}
        aria-label={`Jahresfortschritt ${percent}%`}
      >
        <Box className="spv2-employee-stats-gauge-center">
          <strong>{formatHours(plannedHours)}</strong>
          <span>von {formatHours(targetHours)}</span>
        </Box>
      </Box>
      <Box className="spv2-employee-stats-gauge-foot">
        <span className={Number(plannedHours || 0) > Number(targetHours || 0) ? "is-warning" : ""}>
          <IconTime fontSize="small" />
          {getRemainingLabel(plannedHours, targetHours)}
        </span>
        <em>
          {percent}% des Solls - {vacationDays || 0} Ferientage
        </em>
      </Box>
    </Box>
  );
}

function YearPaceChart({ months, activeMonthIndex }) {
  let cumulativePlanned = 0;
  let cumulativeTarget = 0;
  const series = months.map((month) => {
    cumulativePlanned += Number(month.plannedHours || 0);
    cumulativeTarget += Number(month.targetHours || 0);
    return {
      ...month,
      cumulativePlanned,
      cumulativeTarget,
    };
  });
  const maxHours = Math.max(
    1,
    ...series.map((month) => Math.max(month.cumulativePlanned || 0, month.cumulativeTarget || 0))
  );
  const toX = (monthIndex) => (series.length <= 1 ? 0 : (Number(monthIndex || 0) / 11) * 100);
  const toY = (hours) => 58 - (Number(hours || 0) / maxHours) * 50;
  const plannedPoints = series.map((month) => `${toX(month.monthIndex)},${toY(month.cumulativePlanned)}`).join(" ");
  const targetPoints = series.map((month) => `${toX(month.monthIndex)},${toY(month.cumulativeTarget)}`).join(" ");
  const plannedAreaPoints = plannedPoints ? `0,58 ${plannedPoints} 100,58` : "";
  const activeX = toX(activeMonthIndex);

  return (
    <Box className="spv2-employee-stats-pace">
      <Box className="spv2-employee-stats-pace-head">
        <span>Jahresverlauf</span>
        <Box className="spv2-employee-stats-pace-legend" aria-hidden="true">
          <span>
            <i className="is-planned" />
            Ist
          </span>
          <span>
            <i className="is-target" />
            Soll
          </span>
        </Box>
      </Box>
      <Box className="spv2-employee-stats-pace-plot">
        <svg
          className="spv2-employee-stats-pace-svg"
          viewBox="0 0 100 64"
          preserveAspectRatio="none"
          role="img"
          aria-label="Kumulierter Jahresverlauf"
        >
          <line x1="0" y1="8" x2="100" y2="8" />
          <line x1="0" y1="33" x2="100" y2="33" />
          <line x1="0" y1="58" x2="100" y2="58" />
          {plannedAreaPoints ? <polygon className="is-planned-area" points={plannedAreaPoints} /> : null}
          <line className="is-active-month" x1={activeX} y1="8" x2={activeX} y2="58" />
          <polyline className="is-target" points={targetPoints} />
          <polyline className="is-planned" points={plannedPoints} />
        </svg>
        <Box className="spv2-employee-stats-pace-dots" aria-hidden="true">
          <span className="spv2-employee-stats-pace-zero">0h</span>
          {series.map((month) => (
            <span
              key={month.monthIndex}
              className={`spv2-employee-stats-pace-dot${month.monthIndex === activeMonthIndex ? " is-active" : ""}`}
              style={{
                left: `${toX(month.monthIndex)}%`,
                top: `${(toY(month.cumulativePlanned) / 64) * 100}%`,
              }}
              title={`${month.fullLabel}: ${formatHours(month.cumulativePlanned)} Ist / ${formatHours(
                month.cumulativeTarget
              )} Soll`}
            />
          ))}
        </Box>
      </Box>
      <Box className="spv2-employee-stats-pace-months" aria-hidden="true">
        {series.map((month) => (
          <span key={month.monthIndex} className={month.monthIndex === activeMonthIndex ? "is-active" : ""}>
            {month.label}
          </span>
        ))}
      </Box>
    </Box>
  );
}

function MonthTabs({ months, activeMonthIndex, onMonthSelect }) {
  return (
    <Box className="spv2-employee-stats-month-tabs" role="tablist" aria-label="Monat wählen">
      {months.map((month) => (
        <button
          key={month.monthIndex}
          type="button"
          role="tab"
          aria-selected={month.monthIndex === activeMonthIndex}
          className={month.monthIndex === activeMonthIndex ? "is-active" : ""}
          onClick={() => onMonthSelect(month.monthIndex)}
        >
          {month.label}
        </button>
      ))}
    </Box>
  );
}

function MonthStat({ value, label, icon, tone = "" }) {
  return (
    <Box className={`spv2-employee-stats-month-stat${tone ? ` is-${tone}` : ""}`}>
      <span aria-hidden="true">{icon}</span>
      <Box>
        <strong>{value}</strong>
        <em>{label}</em>
      </Box>
    </Box>
  );
}

function MonthDetail({ month, calendarYear }) {
  const plannedHours = Number(month?.plannedHours || 0);
  const targetHours = Number(month?.targetHours || 0);
  const percent = getPercent(plannedHours, targetHours);
  const balance = plannedHours - targetHours;

  return (
    <Box className="spv2-employee-stats-month-detail">
      <Box className="spv2-employee-stats-month-focus">
        <Box className="spv2-employee-stats-month-focus-head">
          <span>
            Erfasste Zeit im {month?.fullLabel || month?.label} <em>{calendarYear}</em>
          </span>
          <strong>
            {formatHours(plannedHours)}
            <em> / {formatHours(targetHours)}</em>
          </strong>
        </Box>
        <Box className="spv2-employee-stats-month-progress" aria-hidden="true">
          <span style={{ width: `${clampPercent(percent)}%` }} />
        </Box>
        <Box className="spv2-employee-stats-month-focus-meta">
          <span>{percent}% des Monatssolls erfasst</span>
          <strong className={balance > 0 ? "is-warning" : ""}>{getRemainingLabel(plannedHours, targetHours)}</strong>
        </Box>
      </Box>

      <Box className="spv2-employee-stats-month-stats">
        <MonthStat value={month?.workDays || 0} label="Arbeitstage" icon={<CalendarMonthOutlinedIcon fontSize="small" />} />
        <MonthStat
          value={month?.shiftCount || 0}
          label="Einsätze"
          icon={<WorkOutlineOutlinedIcon fontSize="small" />}
          tone="shifts"
        />
        <MonthStat
          value={month?.vacationDays || 0}
          label="Ferien"
          icon={<IconHoliday fontSize="small" />}
          tone="vacation"
        />
        <MonthStat
          value={month?.nonVacationAbsenceDays || 0}
          label="Absenzen"
          icon={<EventBusyOutlinedIcon fontSize="small" />}
          tone="absence"
        />
      </Box>
    </Box>
  );
}

export default function EmployeeStatsPanel({
  open,
  employees = [],
  shifts = [],
  referenceDate,
  selectedEmployeeId,
  workingDaysPerWeek = 5,
  absenceOptions = [],
  vacationAbsenceIds = new Set(),
  userHolidays = [],
  onLoadPlanRange,
  onEmployeeSelect,
  onClose,
}) {
  const [monthDate, setMonthDate] = useState(() => referenceDate || new Date());
  const [employeeQuery, setEmployeeQuery] = useState("");

  /*
    Die Auswertung deckt das ganze Kalenderjahr ab — Jahressumme und der
    Zwölf-Monats-Verlauf. Das Raster lädt nur die angezeigte Periode, also holt
    dieses Fenster sich sein Jahr selbst, sobald es aufgeht.
  */
  const statsYear = (monthDate instanceof Date ? monthDate : new Date()).getFullYear();
  const yearRange = usePlanRange({
    enabled: open,
    startDate: `${statsYear}-01-01`,
    endDate: `${statsYear}-12-31`,
    onLoadPlanRange,
    fallbackShifts: shifts,
    fallbackUserHolidays: userHolidays,
  });
  const selectedEmployee = useMemo(
    () =>
      employees.find((employee) => String(employee?.id || "") === String(selectedEmployeeId || "")) ||
      employees[0] ||
      null,
    [employees, selectedEmployeeId]
  );
  const stats = useMemo(
    () =>
      selectedEmployee
        ? buildEmployeeStatsModel({
            employee: selectedEmployee,
            shifts: yearRange.shifts,
            monthDate,
            workingDaysPerWeek,
            absenceOptions,
            vacationAbsenceIds,
            userHolidays: yearRange.userHolidays,
          })
        : null,
    [
      absenceOptions,
      monthDate,
      selectedEmployee,
      vacationAbsenceIds,
      workingDaysPerWeek,
      yearRange.shifts,
      yearRange.userHolidays,
    ]
  );

  useEffect(() => {
    if (!open) return;
    setMonthDate(referenceDate || new Date());
  }, [open, referenceDate]);

  const activeMonth = stats?.monthlySeries?.[stats?.monthIndex] || stats?.month || null;
  const employmentPercent = getEmploymentPercent(selectedEmployee, workingDaysPerWeek);
  const weeklyHours = Number(selectedEmployee?.weeklyHours || 0);
  const handleMonthSelect = (monthIndex) => {
    const year = stats?.calendarYear || new Date().getFullYear();
    setMonthDate(new Date(year, monthIndex, 1));
  };

  return (
    <FloatingPanel
      open={open}
      title="Mitarbeitenden-Statistik"
      icon={<QueryStatsOutlinedIcon fontSize="small" />}
      onClose={onClose}
      panelId="shiftplan_v2_employee_stats"
      width={900}
      height={650}
      minWidth={760}
      minHeight={520}
      className="spv2-employee-stats-floating-panel"
      bodyClassName="spv2-employee-stats-panel-body"
    >
      <Box className="spv2-employee-stats-panel">
        <EmployeeList
          employees={employees}
          selectedEmployeeId={selectedEmployee?.id}
          query={employeeQuery}
          onQueryChange={setEmployeeQuery}
          onSelect={onEmployeeSelect}
        />

        <Box className="spv2-employee-stats-content">
          {selectedEmployee && stats ? (
            <>
              <Box className="spv2-employee-stats-topline">
                <Box className="spv2-employee-stats-person">
                  <AppPersonBadge
                    className="spv2-employee-stats-person-avatar"
                    name={selectedEmployee.name}
                    initials={getEmployeeInitials(selectedEmployee.name)}
                    size="xl"
                  />
                  <Box minWidth={0}>
                    <Typography className="spv2-employee-stats-person-name">{selectedEmployee.name}</Typography>
                    <Typography className="spv2-employee-stats-person-meta">
                      {formatHours(weeklyHours)} / Woche - {employmentPercent ? `${employmentPercent}% Pensum` : "Pensum offen"}
                    </Typography>
                  </Box>
                </Box>

                <span className="spv2-employee-stats-grade-chip">
                  <QueryStatsOutlinedIcon fontSize="small" />
                  {employmentPercent ? `Beschäftigungsgrad ${employmentPercent}%` : "Beschäftigungsgrad offen"}
                </span>
              </Box>

              <Box className="spv2-employee-stats-year-card">
                <SectionTitle title="Jahresfortschritt" hint="Ist gegenüber Jahressoll" />
                <Box className="spv2-employee-stats-year-hero">
                  <YearGauge
                    plannedHours={stats.year.plannedHours}
                    targetHours={stats.year.targetHours}
                    vacationDays={stats.year.vacationDays}
                  />
                  <YearPaceChart months={stats.monthlySeries} activeMonthIndex={stats.monthIndex} />
                </Box>
              </Box>

              <Box className="spv2-employee-stats-month-card">
                <SectionTitle title="Einzelner Monat" hint="Monat wählen" />
                <MonthTabs
                  months={stats.monthlySeries}
                  activeMonthIndex={stats.monthIndex}
                  onMonthSelect={handleMonthSelect}
                />
                <MonthDetail month={activeMonth} calendarYear={stats.calendarYear} />
              </Box>
            </>
          ) : (
            <Box className="spv2-employee-stats-empty-note">Keine Mitarbeitenden vorhanden.</Box>
          )}
        </Box>
      </Box>
    </FloatingPanel>
  );
}
